import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import {
  SourcesDialogComponent,
  SourcesDialogData,
  SourcesDialogResult,
} from '../sources-dialog/sources-dialog.component';

@Component({
  selector: 'app-sources-button',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  providers: [DialogService],
  template: `
    <p-button
      [label]="label"
      [size]="size"
      [variant]="variant"
      [rounded]="rounded"
      [icon]="icon"
      [severity]="severity"
      [disabled]="disabled"
      (click)="handleClick()" />
  `,
})
export class SourcesButtonComponent {
  @Input() sources: (string | null)[] = [null];
  @Input() maxSourceItems = 5;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() label = 'Sources';
  @Input() size: 'small' | 'large' | undefined = 'large';
  @Input() variant: 'text' | 'outlined' | 'raised' | undefined = 'outlined';
  @Input() rounded = true;
  @Input() icon = 'pi pi-book';
  @Input() severity:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warn'
    | 'help'
    | 'danger'
    | 'contrast'
    | undefined = 'secondary';
  @Input() dialogHeader = 'Manage Sources';
  @Input() dialogWidth = '45rem';

  @Output() sourcesChange = new EventEmitter<(string | null)[]>();

  private dialogRef: DynamicDialogRef | undefined;

  constructor(
    private dialogService: DialogService,
    private destroyRef: DestroyRef,
  ) {}

  handleClick() {
    this.dialogRef = this.dialogService.open(SourcesDialogComponent, {
      modal: true,
      closable: true,
      contentStyle: { overflow: 'auto', width: this.dialogWidth },
      header: this.dialogHeader,
      closeOnEscape: true,
      dismissableMask: true,
      data: {
        sources: this.sources,
        maxSourceItems: this.maxSourceItems,
        readonly: this.readonly,
      } as SourcesDialogData,
    });

    this.dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: SourcesDialogResult) => {
        if (!result || result.action === 'cancel') return;

        // Emit the updated sources
        this.sourcesChange.emit(result.sources);
      });
  }
}
